import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    collection,
    getDocs
} from 'firebase/firestore';
import { db } from '../Context/firebaseConfig';
import TheNav from '../Components/navbar';
import SEO, { SITE_URL, breadcrumbSchema } from "../Components/SEO";
import { Sparkles, UserRound } from "lucide-react";
import { getCategorySlug } from "../utils/categorySlugs";
import { getGameSeoCopy } from "../utils/seoContent";
import EditorSpotlightCard, {
    SpotlightEmptyState,
} from "../Components/EditorSpotlightCard";
import {
    getGameSpotlightEditors,
    isEditorFeaturedActive,
    sortEditorsForFeaturedSurface,
} from "../utils/editorSpotlight";
import { listPublicProfiles } from "../services/publicProfiles";

const LIMIT = 8;
const MINT = "#10b981";
const PURPLE = "#a855f7";
const BG = "#0a0f0d";
const SURFACE = "#141a17";
const HAIRLINE = "rgba(255,255,255,0.08)";

function EditorCard({ editor }) {
    const profile = editor.profileInfo || {};
    const username = profile.username || "editor";

    return (
        <Link
            to={`/editor/${username}`}
            className="group relative overflow-hidden rounded-2xl border transition-all duration-300 hover:-translate-y-1"
            style={{
                borderColor: HAIRLINE,
                backgroundColor: SURFACE,
            }}
        >
            <div className="relative aspect-[4/3] overflow-hidden">
                {profile.profilePicUrl ? (
                    <img
                        src={profile.profilePicUrl}
                        alt={username}
                        type="image"
                        fetchPriority="high"
                        decoding="async"
                        loading="eager"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                ) : (
                    <div
                        className="grid h-full w-full place-items-center"
                        style={{
                            background: `linear-gradient(135deg, ${PURPLE}33, ${MINT}22)`,
                        }}
                    >
                        <UserRound className="h-14 w-14 text-white/50" />
                    </div>
                )}

                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.78) 100%)",
                    }}
                />

                <div className="absolute left-3 top-3">
                    <div className="flex flex-wrap gap-2">
                        <span
                            className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                            style={{
                                backgroundColor: MINT,
                                color: BG,
                            }}
                        >
                            Editor
                        </span>
                        {isEditorFeaturedActive(editor) && (
                            <span className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.08] px-3 py-1 text-xs font-semibold text-white">
                                Featured
                            </span>
                        )}
                    </div>
                </div>

                {profile.availableForAll && (
                    <div className="absolute right-3 top-3">
                        <span
                            className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold"
                            style={{
                                backgroundColor: `${PURPLE}30`,
                                color: "#fff",
                                border: `1px solid ${PURPLE}66`,
                            }}
                        >
                            <Sparkles className="h-3 w-3" />
                            All
                        </span>
                    </div>
                )}
            </div>

            <div className="p-5">
                <h2 className="truncate text-lg font-semibold text-center text-white">
                    @{username}
                </h2>
                <br/>
            </div>
        </Link>
    );
}

function EditorSkeletonCard() {
    return (
        <div
            className="overflow-hidden rounded-2xl border"
            style={{
                borderColor: HAIRLINE,
                backgroundColor: SURFACE,
            }}
        >
            <div className="aspect-[4/3] animate-pulse bg-white/[0.04]" />
            <div className="space-y-3 p-5">
                <div className="h-5 w-2/3 animate-pulse rounded bg-white/[0.06]" />
                <div className="h-7 w-24 animate-pulse rounded-full bg-white/[0.05]" />
                <div className="h-4 w-28 animate-pulse rounded bg-white/[0.05]" />
            </div>
        </div>
    );
}

export default function EditorsPerGame() {
    const { gameId: categorySlug } = useParams();
    const navigate = useNavigate();
    const [editors, setEditors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [categoryName, setCategoryName] = useState('');
    const [categoryImage, setCategoryImage] = useState('');
    const [canonicalCategorySlug, setCanonicalCategorySlug] = useState('');
    const [categoryLoading, setCategoryLoading] = useState(true);
    const [spotlightEditors, setSpotlightEditors] = useState([]);
    const [spotlightLoading, setSpotlightLoading] = useState(false);

    useEffect(() => {
        setCategoryLoading(true);
        setCategoryName('');
        setCategoryImage('');
        setCanonicalCategorySlug('');
        setEditors([]);
        setHasMore(true);

        getDocs(collection(db, 'Categories'))
            .then(categoriesSnap => {
                const categoryDocs = categoriesSnap.docs.map(categoryDoc => ({
                    id: categoryDoc.id,
                    ...categoryDoc.data(),
                }));
                const matchedCategory = categoryDocs.find(category => {
                    const slug = getCategorySlug(category);
                    return slug === categorySlug || category.id === categorySlug;
                });

                if (matchedCategory) {
                    const matchedSlug = getCategorySlug(matchedCategory);
                    setCategoryName(matchedCategory.categoryName || '');
                    setCategoryImage(matchedCategory.categoryImage || '');
                    setCanonicalCategorySlug(matchedSlug);

                    if (matchedSlug && matchedSlug !== categorySlug) {
                        navigate(`/editors/${matchedSlug}`, { replace: true });
                    }
                }
            })
            .catch(console.error)
            .finally(() => {
                setCategoryLoading(false);
            });
    }, [categorySlug, navigate]);

    const fetchEditors = useCallback(async () => {
        if (!categoryName || loading || !hasMore) return;

        setLoading(true);
        setSpotlightLoading(true);
        try {
            const profiles = await listPublicProfiles({
                role: 'Editor',
                mainGame: categoryName,
            });
            setEditors(sortEditorsForFeaturedSurface(
                profiles,
                `game-directory:${categoryName}`
            ));
            setSpotlightEditors(getGameSpotlightEditors(profiles, categoryName, 3));
            setHasMore(false);
        } catch (err) {
            console.error('Failed to fetch editors:', err);
        } finally {
            setLoading(false);
            setSpotlightLoading(false);
        }
    }, [categoryName, loading, hasMore]);

    useEffect(() => {
        if (categoryName) {
            fetchEditors();
        }
    }, [categoryName, fetchEditors]);

    const pagePath = `/editors/${canonicalCategorySlug || categorySlug}`;
    const gameCopy = getGameSeoCopy(canonicalCategorySlug || categorySlug, categoryName);
    const pageTitle = categoryName
        ? `${gameCopy.title} | Kre8`
        : "Gaming Video Editors and Clippers for Twitch Streamers | Kre8";
    const pageDescription = categoryName
        ? gameCopy.description
        : "Find gaming video editors and clippers by category for Twitch streamers, clipping campaigns, YouTube gaming videos, Shorts, and TikToks.";

    return (
        <>
            <SEO
                title={pageTitle}
                description={pageDescription}
                canonicalPath={pagePath}
                image={categoryImage || undefined}
                structuredData={[
                    {
                        "@context": "https://schema.org",
                        "@type": "CollectionPage",
                        name: pageTitle,
                        description: pageDescription,
                        url: `${SITE_URL}${pagePath}`,
                    },
                    breadcrumbSchema([
                        { name: "Home", path: "/" },
                        { name: "Gaming Video Editors", path: "/gaming-video-editors" },
                        {
                            name: categoryName || "Editors",
                            path: pagePath,
                        },
                    ]),
                ]}
            />

            <TheNav />

            <main
                className="min-h-screen px-6 py-12 sm:py-16"
                style={{
                    backgroundColor: BG,
                    color: "#e5e7eb",
                }}
            >
                <div className="mx-auto max-w-7xl">
                    <div className="text-center">
                        <p
                            className="text-xs font-semibold uppercase tracking-wider"
                            style={{ color: MINT }}
                        >
                            Twitch editors and clippers
                        </p>

                        <h1 className="mt-3 text-4xl font-bold tracking-tight text-white sm:text-5xl">
                            {categoryLoading ? "Loading Editors" : gameCopy.title}
                        </h1>

                        <p className="mx-auto mt-4 max-w-2xl text-zinc-400">
                            {categoryName
                                ? gameCopy.description
                                : "Browse stream editors and clippers who specialize in Twitch VODs, gaming clips, clipping campaigns, Shorts, TikToks, Reels, and YouTube gaming videos."}
                        </p>
                    </div>

                    <div className="mx-auto mt-8 flex max-w-4xl flex-wrap justify-center gap-3">
                        <Link
                            to="/hire-video-editor-for-twitch"
                            className="rounded-full border px-4 py-2 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/5"
                            style={{ borderColor: HAIRLINE }}
                        >
                            Hire a Twitch video editor
                        </Link>
                        <Link
                            to="/gaming-video-editors"
                            className="rounded-full border px-4 py-2 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/5"
                            style={{ borderColor: HAIRLINE }}
                        >
                            Browse all Twitch editors
                        </Link>
                        <Link
                            to="/clipping-campaigns"
                            className="rounded-full border px-4 py-2 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/5"
                            style={{ borderColor: HAIRLINE }}
                        >
                            Clipping campaigns
                        </Link>
                        <Link
                            to="/gaming-video-editors"
                            className="rounded-full border px-4 py-2 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/5"
                            style={{ borderColor: HAIRLINE }}
                        >
                            Editors by game
                        </Link>
                    </div>

                    <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {!categoryLoading && (
                            <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4">
                                <div className="mb-6 text-center">
                                    <p
                                        className="text-xs font-semibold uppercase tracking-wider"
                                        style={{ color: MINT }}
                                    >
                                        Editor Elite
                                    </p>
                                    <h2 className="mt-2 text-2xl font-bold tracking-tight text-white">
                                        {categoryName} Spotlight
                                    </h2>
                                    <p className="mx-auto mt-2 max-w-2xl text-sm text-zinc-400">
                                        Spotlight editors are featured here for their selected main game so creators can find the strongest fits faster.
                                    </p>
                                </div>

                                {spotlightLoading ? (
                                    <div className="grid gap-4 lg:grid-cols-3">
                                        {Array.from({ length: 3 }).map((_, index) => (
                                            <div
                                                key={index}
                                                className="h-56 animate-pulse rounded-3xl border border-white/10 bg-white/[0.03]"
                                            />
                                        ))}
                                    </div>
                                ) : spotlightEditors.length > 0 ? (
                                    <div className="grid gap-4 lg:grid-cols-3">
                                        {spotlightEditors.map((editor) => (
                                            <EditorSpotlightCard
                                                key={`spotlight-${editor.id}`}
                                                editor={editor}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <SpotlightEmptyState copy={`No ${categoryName || "game"} spotlight editors are active yet.`} />
                                )}
                                <p className="mt-5 text-center text-xs font-semibold uppercase tracking-wider text-zinc-500">
                                    Spotlight slots rotate daily across active editors with featured priority still weighted highest.
                                </p>
                            </div>
                        )}

                        {editors.map(editor => (
                            <EditorCard key={editor.id} editor={editor} />
                        ))}

                        {loading && editors.length === 0 && (
                            Array.from({ length: LIMIT }).map((_, index) => (
                                <EditorSkeletonCard key={index} />
                            ))
                        )}
                    </div>

                    {loading && editors.length > 0 && (
                        <p className="mt-8 text-center text-zinc-500">
                            Loading more editors...
                        </p>
                    )}

                    {!loading && !hasMore && editors.length > 0 && (
                        <p className="mt-8 text-center text-zinc-500">
                            All available editors loaded.
                        </p>
                    )}

                    {!loading && !hasMore && editors.length === 0 && (
                        <div className="mt-20 text-center">
                            <p className="text-zinc-500">
                                No editors found.
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
}
